// src/pages/EmpList.jsx
import React, { useEffect, useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import AppConst from '../apis/appconst';
import NewEmp from '../models/NewEmp';
import DelBox from '../comps/DelBox';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';

const downloadCsv = (rows = [], filename = 'employees.csv') => {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const csv = [
    keys.join(','),
    ...rows.map(r => keys.map(k => {
      const val = r[k] === null || r[k] === undefined ? '' : String(r[k]).replace(/"/g, '""');
      return `"${val}"`;
    }).join(','))
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const EmpList = () => {
  const [formData, setFormData] = useState({ l1: [], search_key: '', page_no: 0 });
  const [departments, setDepartments] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUserId, setAdminUserId] = useState(null);
  const [editItem, setEditItem] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('list');

  // Modal visibility states
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);

  // Password change form state
  const [passwordData, setPasswordData] = useState({
    current_password: '', new_password: '', confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // ===== SALARY TAB STATE =====
  const [salaryStructures, setSalaryStructures] = useState({}); // keyed by user_id
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [payrollStats, setPayrollStats] = useState({
    paid_total: 0, pending_total: 0, paid_count: 0, pending_count: 0, total_records: 0
  });
  const [salarySearch, setSalarySearch] = useState('');
  const [salaryMonthFilter, setSalaryMonthFilter] = useState('');
  const [salarySetupSearch, setSalarySetupSearch] = useState(''); // NEW: search for Salary Structure Setup table
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [selectedEmpForSalary, setSelectedEmpForSalary] = useState(null);
  const [salaryForm, setSalaryForm] = useState({
    basic: 0, hra: 0, da: 0, allowances: 0, pf: 0, tax: 0, other_deductions: 0,
  });
  const [salarySaving, setSalarySaving] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payForm, setPayForm] = useState({
    month: '', working_days: 30, present_days: 30, bonus: 0, advance_deduction: 0, remarks: '',
  });
  const [paySaving, setPaySaving] = useState(false);

  // load roles
  const fetchRoles = async () => {
    try {
      const res = await axios.get(AppConst.BASE_URL + AppConst.GetAllRole);
      if (res.data && res.data.status) setDepartments(res.data.list || []);
    } catch (err) {
      console.error('fetchRoles error', err);
      toast.error('Error fetching roles');
    }
  };

  // load users
  const loadUsers = async () => {
    try {
      const fd = new FormData();
      fd.append('type', 'user');
      fd.append('search_key', formData.search_key || '');
      fd.append('page_no', formData.page_no + '');
      const res = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.status) {
        setFormData(prev => ({ ...prev, l1: Array.isArray(res.data.records) ? res.data.records : [] }));
      } else {
        setFormData(prev => ({ ...prev, l1: [] }));
      }
    } catch (err) {
      console.error('loadUsers error', err);
      toast.error('Failed to load users');
      setFormData(prev => ({ ...prev, l1: [] }));
    }
  };

  // ===== API: load all salary structures =====
  const loadSalaryStructures = async () => {
    try {
      const res = await axios.post(AppConst.BASE_URL + AppConst.GetSalaryStructures);
      if (res.data && res.data.status) {
        const map = {};
        (res.data.records || []).forEach(r => { map[r.user_id] = r; });
        setSalaryStructures(map);
      }
    } catch (err) {
      console.error('loadSalaryStructures error', err);
    }
  };

  // ===== API: load payroll list =====
  const loadPayroll = async () => {
    try {
      const fd = new FormData();
      if (salarySearch) fd.append('search_key', salarySearch);
      if (salaryMonthFilter) fd.append('month_filter', salaryMonthFilter);
      const res = await axios.post(AppConst.BASE_URL + AppConst.GetPayrollList, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.status) {
        setPayrollRecords(res.data.records || []);
        setPayrollStats(res.data.stats || { paid_total: 0, pending_total: 0, paid_count: 0, pending_count: 0, total_records: 0 });
      }
    } catch (err) {
      console.error('loadPayroll error', err);
    }
  };

  useEffect(() => {
    const stored = sessionStorage.getItem('ldata');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setIsAdmin(parsed?.role_id === 1 || parsed?.role_id === '1');
        setAdminUserId(parsed?.user_id || null);
      } catch (e) { /* ignore */ }
    }
    fetchRoles();
    loadUsers();
    loadSalaryStructures();
    loadPayroll();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line
  }, [formData.search_key, formData.page_no]);

  // reload payroll when filters change
  useEffect(() => {
    if (activeTab === 'salary') loadPayroll();
    // eslint-disable-next-line
  }, [salarySearch, salaryMonthFilter]);

  const getRoleNameById = (id) => {
    if (!id) return 'Unknown Role';
    const r = departments.find(d => String(d.id) === String(id));
    return r ? r.role_name : 'Unknown Role';
  };

  const openAddModal = () => {
    setEditItem(null);
    setShowModal(true);
    const el = document.getElementById('addbt453');
    if (el) el.click();
  };

  const handleEdit = (row) => {
    setEditItem(row);
    setShowModal(true);
    const el = document.getElementById('addbt453');
    if (el) el.click();
  };

  const handleChangePassword = (row) => {
    if (!isAdmin) { toast.error('Only admin can change passwords.'); return; }
    setSelectedUserForPassword(row);
    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    setShowCurrentPassword(false); setShowNewPassword(false); setShowConfirmPassword(false);
    setShowPasswordModal(true);
  };

  const handleDelete = async (row) => {
    if (!isAdmin) { toast.error('Only admin can delete.'); return; }
    const result = await Swal.fire({
      title: `Delete ${row.fname || 'this user'}?`,
      text: 'This action cannot be undone.',
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Yes, delete', cancelButtonText: 'Cancel', reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      const fd = new FormData();
      fd.append('user_id', row.user_id + '');
      const res = await axios.post(AppConst.BASE_URL + AppConst.delUserUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.status) {
        toast.success('Deleted');
        await loadUsers();
      } else {
        toast.error(res.data?.error || 'Delete failed');
      }
    } catch (err) { console.error('delete error', err); toast.error('Delete failed'); }
  };

  const exportPdf = () => {
    const rows = filteredRows;
    const doc = new jsPDF('p', 'pt', 'a4');
    doc.setFontSize(14);
    doc.text('Employee List', 40, 40);
    const head = [['Sl. No.', 'Name', 'Department', 'Email', 'Mobile', 'Password', 'DOB']];
    const body = rows.map((r, idx) => {
      const password = r.password_display || r.password_plain || (r.password ? 'Encrypted' : 'N/A');
      return [String(idx + 1), r.fname || '', getRoleNameById(r.department), r.email || '', r.mobile || '', password, r.dob || ''];
    });
    autoTable(doc, {
      startY: 60, head, body,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [215, 100, 34] },
      margin: { left: 30, right: 30 }, theme: 'grid',
    });
    doc.save(`employees_${Date.now()}.pdf`);
  };

  const filteredRows = useMemo(() => {
    const s = (formData.search_key || '').trim().toLowerCase();
    if (!s) return formData.l1 || [];
    return (formData.l1 || []).filter(r =>
      (r.fname || '').toString().toLowerCase().includes(s) ||
      (r.lname || '').toString().toLowerCase().includes(s) ||
      (r.email || '').toString().toLowerCase().includes(s) ||
      (r.mobile || '').toString().toLowerCase().includes(s) ||
      (`SDC000101${r.user_id}` || '').toLowerCase().includes(s)
    );
  }, [formData.l1, formData.search_key]);

  const columns = useMemo(() => ([
    { name: 'Sl. No.', selector: (row, idx) => idx + 1, width: '80px' },
    { name: 'Name', selector: row => row.fname || '', sortable: true, grow: 1 },
    { name: 'Department', selector: row => getRoleNameById(row.department), sortable: true, width: '180px' },
    {
      name: 'Branch',
      selector: row => row.branch_name || '',
      cell: row => row.branch_name
        ? <span className="badge bg-primary-subtle text-primary">{row.branch_name}</span>
        : <span className="text-muted">—</span>,
      sortable: true,
      width: '160px'
    },
    { name: 'Email', selector: row => row.email || '', sortable: true, width: '220px' },
    { name: 'Mobile', selector: row => row.mobile || '', width: '140px' },
    { name: 'DOB', selector: row => row.dob || '', width: '120px' },
    {
      name: 'Action',
      cell: row => (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(row)}
            disabled={!isAdmin} style={{ opacity: !isAdmin ? 0.5 : 1 }} title="Edit Employee">Edit</button>
          <button className="btn btn-sm btn-outline-warning" onClick={() => handleChangePassword(row)}
            disabled={!isAdmin} style={{ opacity: !isAdmin ? 0.5 : 1 }} title="Change Password">
            <i className="ri-lock-password-line"></i>
          </button>
          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row)}
            disabled={!isAdmin} style={{ opacity: !isAdmin ? 0.5 : 1 }} title="Delete Employee">Delete</button>
        </div>
      ),
      ignoreRowClick: true, allowOverflow: true, button: true, width: '220px'
    }
  ]), [departments, isAdmin, filteredRows]);

  const customStyles = {
    headCells: { style: { backgroundColor: '#f1f5fb', color: '#405189', fontSize: '14px', fontWeight: '700' } },
    rows: { style: { minHeight: '56px' } },
    cells: { style: { paddingLeft: '12px', paddingRight: '12px' } }
  };

  const onNewEmpClose = (refresh = false) => {
    setShowModal(false); setEditItem(null);
    if (refresh) loadUsers();
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin && !passwordData.current_password) {
      Swal.fire({ icon: 'warning', title: 'Required', text: 'Please enter current password' }); return;
    }
    if (!passwordData.new_password) {
      Swal.fire({ icon: 'warning', title: 'Required', text: 'Please enter new password' }); return;
    }
    if (passwordData.new_password.length < 6) {
      Swal.fire({ icon: 'warning', title: 'Invalid', text: 'New password must be at least 6 characters' }); return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      Swal.fire({ icon: 'error', title: 'Mismatch', text: 'Passwords do not match' }); return;
    }
    if (!isAdmin && passwordData.current_password === passwordData.new_password) {
      Swal.fire({ icon: 'warning', title: 'Same Password', text: 'New password must be different' }); return;
    }
    setPasswordLoading(true);
    try {
      const fd = new FormData();
      fd.append('user_id', selectedUserForPassword.user_id);
      fd.append('new_password', passwordData.new_password);
      if (isAdmin && adminUserId) fd.append('admin_user_id', adminUserId);
      if (passwordData.current_password) fd.append('current_password', passwordData.current_password);

      const res = await axios.post(AppConst.BASE_URL + AppConst.ChangePassword, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data && res.data.status) {
        setShowPasswordModal(false); setSelectedUserForPassword(null);
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
        await Swal.fire({ icon: 'success', title: 'Password Changed!', text: res.data.success || 'Done', timer: 3000, timerProgressBar: true });
        await loadUsers();
      } else {
        await Swal.fire({ icon: 'error', title: 'Failed', text: res.data?.success || 'Failed' });
      }
    } catch (err) {
      console.error('Change password error:', err);
      await Swal.fire({ icon: 'error', title: 'Error', text: 'An error occurred.' });
    } finally { setPasswordLoading(false); }
  };

  const onPasswordModalClose = () => {
    setShowPasswordModal(false); setSelectedUserForPassword(null);
    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
  };

  // ========================================================
  // ===================== SALARY LOGIC =====================
  // ========================================================

  const calcStructure = (s) => {
    const basic = +s.basic || 0;
    const hra = +s.hra || 0;
    const da = +s.da || 0;
    const allowances = +s.allowances || 0;
    const pf = +s.pf || 0;
    const tax = +s.tax || 0;
    const other = +s.other_deductions || 0;
    const gross = basic + hra + da + allowances;
    const deductions = pf + tax + other;
    const net = gross - deductions;
    return { gross, deductions, net };
  };

  const openSalaryStructureModal = (emp) => {
    if (!isAdmin) { toast.error('Only admin can set salary.'); return; }
    const existing = salaryStructures[emp.user_id] || {
      basic: 0, hra: 0, da: 0, allowances: 0, pf: 0, tax: 0, other_deductions: 0,
    };
    setSelectedEmpForSalary(emp);
    setSalaryForm({
      basic: existing.basic || 0,
      hra: existing.hra || 0,
      da: existing.da || 0,
      allowances: existing.allowances || 0,
      pf: existing.pf || 0,
      tax: existing.tax || 0,
      other_deductions: existing.other_deductions || 0,
    });
    setShowSalaryModal(true);
  };

  // ===== API: save salary structure =====
  const saveSalaryStructure = async (e) => {
    e.preventDefault();
    if (!selectedEmpForSalary) return;
    if ((+salaryForm.basic || 0) <= 0) {
      Swal.fire({ icon: 'warning', title: 'Invalid', text: 'Basic salary must be greater than 0' });
      return;
    }
    setSalarySaving(true);
    try {
      const fd = new FormData();
      fd.append('user_id', selectedEmpForSalary.user_id);
      fd.append('basic', salaryForm.basic);
      fd.append('hra', salaryForm.hra);
      fd.append('da', salaryForm.da);
      fd.append('allowances', salaryForm.allowances);
      fd.append('pf', salaryForm.pf);
      fd.append('tax', salaryForm.tax);
      fd.append('other_deductions', salaryForm.other_deductions);
      if (adminUserId) fd.append('admin_user_id', adminUserId);

      const res = await axios.post(AppConst.BASE_URL + AppConst.SaveSalaryStructure, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.status) {
        toast.success(res.data.success || 'Saved');
        setShowSalaryModal(false);
        setSelectedEmpForSalary(null);
        await loadSalaryStructures();
      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: res.data?.success || 'Failed to save' });
      }
    } catch (err) {
      console.error('saveSalaryStructure error', err);
      toast.error('Save failed');
    } finally { setSalarySaving(false); }
  };

  const openPayModal = (emp) => {
    if (!isAdmin) { toast.error('Only admin can process payroll.'); return; }
    const struct = salaryStructures[emp.user_id];
    if (!struct) {
      Swal.fire({ icon: 'info', title: 'No Salary Structure', text: 'Please set salary structure first.' });
      return;
    }
    setSelectedEmpForSalary(emp);
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setPayForm({ month, working_days: 30, present_days: 30, bonus: 0, advance_deduction: 0, remarks: '' });
    setShowPayModal(true);
  };

  // ===== API: process payroll =====
  const processPayroll = async (e) => {
    e.preventDefault();
    if (!selectedEmpForSalary) return;
    const workingDays = +payForm.working_days || 30;
    const presentDays = +payForm.present_days || 0;
    if (presentDays > workingDays) {
      Swal.fire({ icon: 'warning', title: 'Invalid', text: 'Present days cannot exceed working days' });
      return;
    }
    setPaySaving(true);
    try {
      const fd = new FormData();
      fd.append('user_id', selectedEmpForSalary.user_id);
      fd.append('emp_name', selectedEmpForSalary.fname || '');
      fd.append('department', getRoleNameById(selectedEmpForSalary.department));
      fd.append('month', payForm.month);
      fd.append('working_days', workingDays);
      fd.append('present_days', presentDays);
      fd.append('bonus', +payForm.bonus || 0);
      fd.append('advance_deduction', +payForm.advance_deduction || 0);
      fd.append('remarks', payForm.remarks || '');
      if (adminUserId) fd.append('admin_user_id', adminUserId);

      const res = await axios.post(AppConst.BASE_URL + AppConst.ProcessPayroll, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.status) {
        toast.success(res.data.success || 'Payslip generated');
        setShowPayModal(false);
        setSelectedEmpForSalary(null);
        await loadPayroll();
      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: res.data?.success || 'Failed to process' });
      }
    } catch (err) {
      console.error('processPayroll error', err);
      toast.error('Failed to process payroll');
    } finally { setPaySaving(false); }
  };

  // ===== API: mark paid =====
  const markAsPaid = async (rec) => {
    const result = await Swal.fire({
      title: 'Mark as Paid?',
      html: `Employee: <b>${rec.emp_name}</b><br/>Month: <b>${rec.month}</b><br/>Net: <b>₹${rec.net_salary}</b>`,
      icon: 'question', showCancelButton: true, confirmButtonText: 'Yes, mark paid',
    });
    if (!result.isConfirmed) return;
    try {
      const fd = new FormData();
      fd.append('payroll_id', rec.payroll_id);
      const res = await axios.post(AppConst.BASE_URL + AppConst.MarkPayrollPaid, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.status) {
        toast.success('Marked as paid');
        await loadPayroll();
      } else {
        toast.error(res.data?.success || 'Failed');
      }
    } catch (err) { toast.error('Failed to mark paid'); }
  };

  // ===== API: delete payroll =====
  const deletePayroll = async (rec) => {
    if (!isAdmin) return;
    const result = await Swal.fire({
      title: 'Delete payslip?',
      text: `${rec.emp_name} — ${rec.month}`,
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      const fd = new FormData();
      fd.append('payroll_id', rec.payroll_id);
      const res = await axios.post(AppConst.BASE_URL + AppConst.DeletePayroll, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.status) {
        toast.success('Deleted');
        await loadPayroll();
      } else {
        toast.error(res.data?.success || 'Failed');
      }
    } catch (err) { toast.error('Failed to delete'); }
  };

  const downloadPayslipPdf = (rec) => {
    const doc = new jsPDF('p', 'pt', 'a4');
    doc.setFontSize(18);
    doc.text('PAYSLIP', 40, 50);
    doc.setFontSize(10);
    doc.text(`For the month: ${rec.month}`, 40, 70);
    doc.text(`Generated: ${rec.created_at ? new Date(rec.created_at).toLocaleDateString() : '-'}`, 40, 85);

    doc.setFontSize(11);
    doc.text(`Employee: ${rec.emp_name}`, 40, 115);
    doc.text(`Department: ${rec.department || '-'}`, 40, 130);
    doc.text(`Employee ID: SDC000101${rec.user_id}`, 40, 145);
    doc.text(`Status: ${rec.status}`, 40, 160);

    autoTable(doc, {
      startY: 180,
      head: [['Earnings', 'Amount (₹)']],
      body: [
        ['Basic', (+rec.basic).toFixed(2)],
        ['HRA', (+rec.hra).toFixed(2)],
        ['DA', (+rec.da).toFixed(2)],
        ['Other Allowances', (+rec.allowances).toFixed(2)],
        ['Bonus', (+rec.bonus).toFixed(2)],
        [{ content: 'Gross (Full)', styles: { fontStyle: 'bold' } }, (+rec.gross_salary).toFixed(2)],
        [{ content: `Earned Gross (${rec.present_days}/${rec.working_days} days)`, styles: { fontStyle: 'bold' } }, (+rec.earned_gross).toFixed(2)],
      ],
      theme: 'grid', headStyles: { fillColor: [64, 81, 137] }, styles: { fontSize: 10 },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 15,
      head: [['Deductions', 'Amount (₹)']],
      body: [
        ['PF', (+rec.pf).toFixed(2)],
        ['Tax', (+rec.tax).toFixed(2)],
        ['Other Deductions', (+rec.other_deductions).toFixed(2)],
        ['Advance', (+rec.advance_deduction).toFixed(2)],
        [{ content: 'Total Deductions', styles: { fontStyle: 'bold' } }, (+rec.total_deductions).toFixed(2)],
      ],
      theme: 'grid', headStyles: { fillColor: [200, 50, 50] }, styles: { fontSize: 10 },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 15,
      body: [
        [
          { content: 'NET SALARY PAYABLE', styles: { fontStyle: 'bold', fontSize: 12 } },
          { content: `₹ ${(+rec.net_salary).toFixed(2)}`, styles: { fontStyle: 'bold', fontSize: 12, halign: 'right' } }
        ]
      ],
      theme: 'grid', styles: { fillColor: [240, 248, 255] },
    });

    if (rec.remarks) {
      doc.setFontSize(9);
      doc.text(`Remarks: ${rec.remarks}`, 40, doc.lastAutoTable.finalY + 30);
    }
    doc.save(`payslip_${rec.emp_name}_${rec.month}.pdf`);
  };

  // Filtered rows for Salary Structure Setup (independent search from main list)
  const salarySetupFilteredRows = useMemo(() => {
    const s = (salarySetupSearch || '').trim().toLowerCase();
    if (!s) return filteredRows;
    return filteredRows.filter(r => {
      const deptName = getRoleNameById(r.department).toLowerCase();
      return (
        (r.fname || '').toString().toLowerCase().includes(s) ||
        (r.lname || '').toString().toLowerCase().includes(s) ||
        (r.email || '').toString().toLowerCase().includes(s) ||
        (r.mobile || '').toString().toLowerCase().includes(s) ||
        deptName.includes(s) ||
        (`SDC000101${r.user_id}` || '').toLowerCase().includes(s)
      );
    });
  }, [filteredRows, salarySetupSearch, departments]);

  // Salary-tab columns
  const salarySetupColumns = useMemo(() => ([
    { name: 'Sl. No.', selector: (row, idx) => idx + 1, width: '80px' },
    { name: 'Employee', selector: row => row.fname || '', sortable: true, grow: 1 },
    { name: 'Department', selector: row => getRoleNameById(row.department), sortable: true, width: '160px' },
    {
      name: 'Gross',
      cell: row => {
        const s = salaryStructures[row.user_id];
        if (!s) return <span className="text-muted">Not Set</span>;
        const { gross } = calcStructure(s);
        return <span className="fw-bold text-success">₹ {gross.toFixed(2)}</span>;
      },
      width: '130px'
    },
    {
      name: 'Deductions',
      cell: row => {
        const s = salaryStructures[row.user_id];
        if (!s) return '-';
        const { deductions } = calcStructure(s);
        return <span className="text-danger">₹ {deductions.toFixed(2)}</span>;
      },
      width: '130px'
    },
    {
      name: 'Net Salary',
      cell: row => {
        const s = salaryStructures[row.user_id];
        if (!s) return '-';
        const { net } = calcStructure(s);
        return <span className="fw-bold" style={{ color: '#405189' }}>₹ {net.toFixed(2)}</span>;
      },
      width: '140px'
    },
    {
      name: 'Status',
      cell: row => salaryStructures[row.user_id]
        ? <span className="badge bg-success-subtle text-success">Configured</span>
        : <span className="badge bg-warning-subtle text-warning">Pending Setup</span>,
      width: '140px'
    },
    {
      name: 'Action',
      cell: row => (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn-sm btn-outline-primary" onClick={() => openSalaryStructureModal(row)} disabled={!isAdmin}>
            <i className="ri-edit-2-line"></i> Setup
          </button>
          <button className="btn btn-sm btn-success" onClick={() => openPayModal(row)} disabled={!isAdmin || !salaryStructures[row.user_id]}>
            <i className="ri-money-rupee-circle-line"></i> Pay
          </button>
        </div>
      ),
      ignoreRowClick: true, allowOverflow: true, button: true, width: '200px'
    }
  ]), [departments, isAdmin, salaryStructures]);

  // Payroll history columns
  const payrollColumns = useMemo(() => ([
    { name: '#', selector: (row, idx) => idx + 1, width: '60px' },
    { name: 'Employee', selector: row => row.emp_name, sortable: true, grow: 1 },
    { name: 'Department', selector: row => row.department || '-', width: '140px' },
    { name: 'Month', selector: row => row.month, sortable: true, width: '110px' },
    { name: 'Days', selector: row => `${row.present_days}/${row.working_days}`, width: '80px' },
    { name: 'Gross', cell: row => <span>₹ {(+row.earned_gross).toFixed(2)}</span>, width: '110px' },
    { name: 'Deductions', cell: row => <span className="text-danger">₹ {(+row.total_deductions).toFixed(2)}</span>, width: '120px' },
    { name: 'Net', cell: row => <span className="fw-bold text-success">₹ {(+row.net_salary).toFixed(2)}</span>, width: '120px' },
    {
      name: 'Status',
      cell: row => row.status === 'Paid'
        ? <span className="badge bg-success">Paid</span>
        : <span className="badge bg-warning">Pending</span>,
      width: '100px'
    },
    {
      name: 'Action',
      cell: row => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <button className="btn btn-sm btn-outline-primary" onClick={() => downloadPayslipPdf(row)} title="Download Payslip">
            <i className="ri-download-line"></i>
          </button>
          {row.status === 'Pending' && (
            <button className="btn btn-sm btn-outline-success" onClick={() => markAsPaid(row)} disabled={!isAdmin} title="Mark Paid">
              <i className="ri-check-line"></i>
            </button>
          )}
          <button className="btn btn-sm btn-outline-danger" onClick={() => deletePayroll(row)} disabled={!isAdmin} title="Delete">
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
      ignoreRowClick: true, allowOverflow: true, button: true, width: '160px'
    }
  ]), [isAdmin]);

  const structurePreview = useMemo(() => calcStructure(salaryForm), [salaryForm]);

  const payPreview = useMemo(() => {
    if (!selectedEmpForSalary) return null;
    const struct = salaryStructures[selectedEmpForSalary.user_id];
    if (!struct) return null;
    const { gross, deductions } = calcStructure(struct);
    const workingDays = +payForm.working_days || 30;
    const presentDays = +payForm.present_days || 0;
    const bonus = +payForm.bonus || 0;
    const advance = +payForm.advance_deduction || 0;
    const perDay = gross / workingDays;
    const earnedGross = +(perDay * presentDays).toFixed(2);
    const finalDeductions = +(deductions + advance).toFixed(2);
    const finalNet = +(earnedGross + bonus - finalDeductions).toFixed(2);
    return { earnedGross, finalDeductions, finalNet, gross };
  }, [payForm, selectedEmpForSalary, salaryStructures]);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="card" id="orderList">

          {/* ====== TABS ====== */}
          <div className="card-header p-0 border-bottom">
            <ul className="nav nav-tabs nav-tabs-custom nav-success" role="tablist" style={{ borderBottom: 'none' }}>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'list' ? 'active' : ''}`}
                  onClick={() => setActiveTab('list')} style={{ padding: '14px 20px', fontWeight: 600 }}>
                  <i className="ri-team-line me-1 align-bottom"></i> Employee List
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'salary' ? 'active' : ''}`}
                  onClick={() => setActiveTab('salary')} style={{ padding: '14px 20px', fontWeight: 600 }}>
                  <i className="ri-money-rupee-circle-line me-1 align-bottom"></i> Employee Salary
                </button>
              </li>
            </ul>
          </div>

          {/* ====== TAB 1 ====== */}
          {activeTab === 'list' && (
            <>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Employees</h5>
                <div>
                  <button className="btn btn-success me-2" onClick={openAddModal}>
                    <i className="ri-add-line align-bottom me-1" /> Add Employee
                  </button>
                  <button className="btn btn-outline-secondary me-2" onClick={() => downloadCsv(filteredRows, 'employees.csv')}>Export CSV</button>
                  <button className="btn btn-outline-primary" onClick={exportPdf}>Export PDF</button>
                </div>
              </div>
              <div className="card-body border border-dashed border-end-0 border-start-0">
                <div className="row g-2 mb-2">
                  <div className="col-md-4">
                    <input className="form-control" placeholder="Search by name, email, contact..." type="text" value={formData.search_key}
                      onChange={(e) => setFormData(prev => ({ ...prev, search_key: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive table-card mb-1">
                  <DataTable columns={columns} data={filteredRows} customStyles={customStyles}
                    noHeader pagination paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 20, 50]} highlightOnHover persistTableHead />
                </div>
              </div>
            </>
          )}

          {/* ====== TAB 2 ====== */}
          {activeTab === 'salary' && (
            <>
              <div className="card-body border border-dashed border-end-0 border-start-0">
                <div className="row g-3">
                  <div className="col-md-3">
                    <div className="p-3 rounded" style={{ background: '#e7f1ff', borderLeft: '4px solid #405189' }}>
                      <div className="text-muted small">Total Records</div>
                      <div className="fs-4 fw-bold" style={{ color: '#405189' }}>{payrollStats.total_records}</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="p-3 rounded" style={{ background: '#e6f7ec', borderLeft: '4px solid #0ab39c' }}>
                      <div className="text-muted small">Paid ({payrollStats.paid_count})</div>
                      <div className="fs-4 fw-bold text-success">₹ {(+payrollStats.paid_total).toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="p-3 rounded" style={{ background: '#fff7e6', borderLeft: '4px solid #f7b84b' }}>
                      <div className="text-muted small">Pending ({payrollStats.pending_count})</div>
                      <div className="fs-4 fw-bold text-warning">₹ {(+payrollStats.pending_total).toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="p-3 rounded" style={{ background: '#f3e8ff', borderLeft: '4px solid #8b5cf6' }}>
                      <div className="text-muted small">Configured Employees</div>
                      <div className="fs-4 fw-bold" style={{ color: '#8b5cf6' }}>{Object.keys(salaryStructures).length}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body pt-3">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <h5 className="mb-0"><i className="ri-settings-3-line me-1"></i> Salary Structure Setup</h5>
                  <div className="d-flex gap-2 align-items-center">
                    <div className="position-relative">
                     
                      <i className="ri-search-line position-absolute"
                        style={{ left: 10, top: '50%', transform: 'translateY(-50%)', color: '#878a99', fontSize: 14 }}></i>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search employee, dept..."
                        value={salarySetupSearch}
                        onChange={e => setSalarySetupSearch(e.target.value)}
                        style={{ width: 240, paddingLeft: 30 }}
                      />
                    </div>
                    {salarySetupSearch && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setSalarySetupSearch('')}
                        title="Clear search"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="table-responsive table-card mb-4">
                  <DataTable columns={salarySetupColumns} data={salarySetupFilteredRows} customStyles={customStyles}
                    noHeader pagination paginationPerPage={5}
                    paginationRowsPerPageOptions={[5, 10, 20]} highlightOnHover persistTableHead
                    noDataComponent={<div className="p-4 text-muted">No employees match your search.</div>} />
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                  <h5 className="mb-0"><i className="ri-file-list-3-line me-1"></i> Payroll History</h5>
                  <div className="d-flex gap-2">
                    <input type="month" className="form-control form-control-sm"
                      value={salaryMonthFilter} onChange={e => setSalaryMonthFilter(e.target.value)} style={{ width: 160 }} />
                    <input type="text" className="form-control form-control-sm" placeholder="Search employee..."
                      value={salarySearch} onChange={e => setSalarySearch(e.target.value)} style={{ width: 200 }} />
                    {(salarySearch || salaryMonthFilter) && (
                      <button className="btn btn-sm btn-outline-secondary"
                        onClick={() => { setSalarySearch(''); setSalaryMonthFilter(''); }}>Clear</button>
                    )}
                  </div>
                </div>

                <div className="table-responsive table-card mb-1">
                  <DataTable columns={payrollColumns} data={payrollRecords} customStyles={customStyles}
                    noHeader pagination paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 20, 50]} highlightOnHover persistTableHead
                    noDataComponent={<div className="p-4 text-muted">No payroll records yet.</div>} />
                </div>
              </div>
            </>
          )}

          <NewEmp item={editItem} show={showModal} onClose={(refresh) => onNewEmpClose(refresh)}
            onAddDepart={() => { setFormData(prev => ({ ...prev, page_no: 0 })); loadUsers(); }} />

          {/* Password Modal */}
          {showPasswordModal && (
            <>
              <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={onPasswordModalClose}></div>
              <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header bg-light">
                      <h5 className="modal-title">Change Password - {selectedUserForPassword?.fname || 'User'}</h5>
                      <button type="button" className="btn-close" onClick={onPasswordModalClose} disabled={passwordLoading}></button>
                    </div>
                    <form onSubmit={handlePasswordSubmit}>
                      <div className="modal-body">
                        {isAdmin && (
                          <div className="alert alert-info mb-3"><i className="ri-information-line me-2"></i>
                            As admin, you can change the password directly without the current password.
                          </div>
                        )}
                        {!isAdmin && (
                          <div className="mb-3">
                            <label className="form-label">Current Password <span className="text-danger">*</span></label>
                            <div className="input-group">
                              <input type={showCurrentPassword ? "text" : "password"} className="form-control"
                                value={passwordData.current_password}
                                onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                                placeholder="Enter current password" disabled={passwordLoading} />
                              <button className="btn btn-outline-secondary" type="button"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                                <i className={`ri-eye${showCurrentPassword ? '-off' : ''}-line`}></i>
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="mb-3">
                          <label className="form-label">New Password <span className="text-danger">*</span></label>
                          <div className="input-group">
                            <input type={showNewPassword ? "text" : "password"} className="form-control"
                              value={passwordData.new_password}
                              onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                              placeholder="Enter new password (min 6)" disabled={passwordLoading} />
                            <button className="btn btn-outline-secondary" type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}>
                              <i className={`ri-eye${showNewPassword ? '-off' : ''}-line`}></i>
                            </button>
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Confirm Password <span className="text-danger">*</span></label>
                          <div className="input-group">
                            <input type={showConfirmPassword ? "text" : "password"} className="form-control"
                              value={passwordData.confirm_password}
                              onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                              placeholder="Re-enter new password" disabled={passwordLoading} />
                            <button className="btn btn-outline-secondary" type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                              <i className={`ri-eye${showConfirmPassword ? '-off' : ''}-line`}></i>
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-light" onClick={onPasswordModalClose} disabled={passwordLoading}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={passwordLoading}>
                          {passwordLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>Changing...</> : <><i className="ri-lock-password-line me-1"></i>Change Password</>}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Salary Structure Modal */}
          {showSalaryModal && (
            <>
              <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={() => !salarySaving && setShowSalaryModal(false)}></div>
              <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                  <div className="modal-content">
                    <div className="modal-header bg-light">
                      <h5 className="modal-title"><i className="ri-settings-3-line me-1"></i>
                        Salary Structure — {selectedEmpForSalary?.fname}</h5>
                      <button type="button" className="btn-close" onClick={() => setShowSalaryModal(false)} disabled={salarySaving}></button>
                    </div>
                    <form onSubmit={saveSalaryStructure}>
                      <div className="modal-body">
                        <div className="row g-3">
                          <div className="col-12"><h6 className="text-success mb-2"><i className="ri-add-circle-line"></i> Earnings</h6></div>
                          <div className="col-md-6">
                            <label className="form-label">Basic Salary <span className="text-danger">*</span></label>
                            <input type="number" min="0" step="0.01" className="form-control" value={salaryForm.basic}
                              onChange={e => setSalaryForm(p => ({ ...p, basic: e.target.value }))} required />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">HRA</label>
                            <input type="number" min="0" step="0.01" className="form-control" value={salaryForm.hra}
                              onChange={e => setSalaryForm(p => ({ ...p, hra: e.target.value }))} />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">DA</label>
                            <input type="number" min="0" step="0.01" className="form-control" value={salaryForm.da}
                              onChange={e => setSalaryForm(p => ({ ...p, da: e.target.value }))} />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Other Allowances</label>
                            <input type="number" min="0" step="0.01" className="form-control" value={salaryForm.allowances}
                              onChange={e => setSalaryForm(p => ({ ...p, allowances: e.target.value }))} />
                          </div>
                          <div className="col-12 mt-3"><h6 className="text-danger mb-2"><i className="ri-subtract-line"></i> Deductions</h6></div>
                          <div className="col-md-4">
                            <label className="form-label">PF</label>
                            <input type="number" min="0" step="0.01" className="form-control" value={salaryForm.pf}
                              onChange={e => setSalaryForm(p => ({ ...p, pf: e.target.value }))} />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">Tax</label>
                            <input type="number" min="0" step="0.01" className="form-control" value={salaryForm.tax}
                              onChange={e => setSalaryForm(p => ({ ...p, tax: e.target.value }))} />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">Other Deductions</label>
                            <input type="number" min="0" step="0.01" className="form-control" value={salaryForm.other_deductions}
                              onChange={e => setSalaryForm(p => ({ ...p, other_deductions: e.target.value }))} />
                          </div>
                          <div className="col-12 mt-3">
                            <div className="p-3 rounded border" style={{ background: '#f8f9fc' }}>
                              <div className="row text-center">
                                <div className="col-md-4">
                                  <div className="text-muted small">Gross</div>
                                  <div className="fs-5 fw-bold text-success">₹ {structurePreview.gross.toFixed(2)}</div>
                                </div>
                                <div className="col-md-4">
                                  <div className="text-muted small">Deductions</div>
                                  <div className="fs-5 fw-bold text-danger">₹ {structurePreview.deductions.toFixed(2)}</div>
                                </div>
                                <div className="col-md-4">
                                  <div className="text-muted small">Net</div>
                                  <div className="fs-5 fw-bold" style={{ color: '#405189' }}>₹ {structurePreview.net.toFixed(2)}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-light" onClick={() => setShowSalaryModal(false)} disabled={salarySaving}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={salarySaving}>
                          {salarySaving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</> : <><i className="ri-save-line me-1"></i>Save Structure</>}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Pay Modal */}
          {showPayModal && selectedEmpForSalary && (
            <>
              <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={() => !paySaving && setShowPayModal(false)}></div>
              <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                  <div className="modal-content">
                    <div className="modal-header bg-light">
                      <h5 className="modal-title"><i className="ri-money-rupee-circle-line me-1"></i>
                        Process Salary — {selectedEmpForSalary.fname}</h5>
                      <button type="button" className="btn-close" onClick={() => setShowPayModal(false)} disabled={paySaving}></button>
                    </div>
                    <form onSubmit={processPayroll}>
                      <div className="modal-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label">Month <span className="text-danger">*</span></label>
                            <input type="month" className="form-control" value={payForm.month}
                              onChange={e => setPayForm(p => ({ ...p, month: e.target.value }))} required />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Working Days</label>
                            <input type="number" min="1" max="31" className="form-control" value={payForm.working_days}
                              onChange={e => setPayForm(p => ({ ...p, working_days: e.target.value }))} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Present Days</label>
                            <input type="number" min="0" max={payForm.working_days} className="form-control" value={payForm.present_days}
                              onChange={e => setPayForm(p => ({ ...p, present_days: e.target.value }))} />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Bonus</label>
                            <input type="number" min="0" step="0.01" className="form-control" value={payForm.bonus}
                              onChange={e => setPayForm(p => ({ ...p, bonus: e.target.value }))} />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Advance Deduction</label>
                            <input type="number" min="0" step="0.01" className="form-control" value={payForm.advance_deduction}
                              onChange={e => setPayForm(p => ({ ...p, advance_deduction: e.target.value }))} />
                          </div>
                          <div className="col-12">
                            <label className="form-label">Remarks</label>
                            <textarea className="form-control" rows="2" value={payForm.remarks}
                              onChange={e => setPayForm(p => ({ ...p, remarks: e.target.value }))} placeholder="Optional notes..."></textarea>
                          </div>
                          {payPreview && (
                            <div className="col-12 mt-2">
                              <div className="p-3 rounded border" style={{ background: '#e7f1ff' }}>
                                <div className="small text-muted mb-2">Calculation Preview</div>
                                <div className="row text-center">
                                  <div className="col-md-3">
                                    <div className="text-muted small">Full Gross</div>
                                    <div className="fw-bold">₹ {payPreview.gross.toFixed(2)}</div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="text-muted small">Earned Gross</div>
                                    <div className="fw-bold text-success">₹ {payPreview.earnedGross.toFixed(2)}</div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="text-muted small">Deductions</div>
                                    <div className="fw-bold text-danger">₹ {payPreview.finalDeductions.toFixed(2)}</div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="text-muted small">NET</div>
                                    <div className="fs-5 fw-bold" style={{ color: '#405189' }}>₹ {payPreview.finalNet.toFixed(2)}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-light" onClick={() => setShowPayModal(false)} disabled={paySaving}>Cancel</button>
                        <button type="submit" className="btn btn-success" disabled={paySaving}>
                          {paySaving ? <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</> : <><i className="ri-file-add-line me-1"></i>Generate Payslip</>}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </>
          )}

          <button id="addbt453" style={{ display: 'none' }} type="button" className="btn btn-success add-btn" data-bs-toggle="modal" data-bs-target="#showModal"></button>
          <DelBox />
        </div>
      </div>
    </div>
  );
};

export default EmpList;